'use client';
import React, { useState } from 'react';
import { Plus, Search, Filter, Eye, Edit3, Send, CheckCircle, Clock, Upload, Trash2, ChevronRight, DollarSign, Lock, X, Save, ArrowRight, Building2, MoreVertical, RefreshCw, Download, TrendingUp, Users, Paperclip, AlertCircle, Info, Check, XCircle } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import MetricCard from '@/components/ui/MetricCard';
import SectionHeader from '@/components/ui/SectionHeader';

// ─── Types ────────────────────────────────────────────────────────────────────

type WorkflowStage =
  | 'Draft' |'Compliance Review' |'Risk Review' |'Finance Review' |'Approved' |'Published' |'Closed';

type MilestoneStatus = 'upcoming' | 'in-progress' | 'complete';
type DocAccess = 'Public' | 'NDA-Gated' | 'Compliance Only' | 'Investors';

interface Milestone {
  id: string;
  name: string;
  due: string;
  status: MilestoneStatus;
  completion: number;
}

interface DisclosureDoc {
  id: string;
  name: string;
  type: string;
  access: DocAccess;
  size: string;
  uploaded: string;
  required: boolean;
  status: 'uploaded' | 'pending' | 'rejected';
}

interface ReviewNote {
  stage: WorkflowStage;
  reviewer: string;
  date: string;
  note: string;
  status: 'approved' | 'rejected' | 'pending' | 'info';
}

interface Campaign {
  id: string;
  name: string;
  issuer: string;
  stage: WorkflowStage;
  target: number;
  raised: number;
  minInvestment: number;
  maxInvestment: number;
  sector: string;
  jurisdiction: string;
  offeringType: string;
  created: string;
  lastUpdated: string;
  milestones: Milestone[];
  documents: DisclosureDoc[];
  reviewNotes: ReviewNote[];
  description: string;
  useOfFunds: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const workflowOrder: WorkflowStage[] = [
  'Draft', 'Compliance Review', 'Risk Review', 'Finance Review', 'Approved', 'Published', 'Closed'
];

const campaigns: Campaign[] = [
  {
    id: 'CAMP-2026-001',
    name: 'Meridian Logistics Hub — Series B',
    issuer: 'Meridian Capital Partners Ltd.',
    stage: 'Published',
    target: 12500000,
    raised: 9875000,
    minInvestment: 10000,
    maxInvestment: 500000,
    sector: 'Logistics & Infrastructure',
    jurisdiction: 'UAE (DIFC)',
    offeringType: 'Equity — Reg D / Reg S',
    created: '2026-05-01',
    lastUpdated: '2026-08-19',
    description: 'Series B equity raise for a 450,000 sqft logistics hub in Jebel Ali Free Zone, targeting institutional and HNWI investors across GCC and Europe.',
    useOfFunds: '60% construction & fit-out · 25% working capital · 15% regulatory & legal',
    milestones: [
      { id: 'ms-001', name: 'Regulatory Disclosure Package Submitted', due: '2026-05-01', status: 'complete', completion: 100 },
      { id: 'ms-002', name: 'NDA Gating Enabled for Institutional Deck', due: '2026-05-15', status: 'complete', completion: 100 },
      { id: 'ms-003', name: '50% Funding Target Achieved', due: '2026-06-30', status: 'complete', completion: 100 },
      { id: 'ms-004', name: 'Independent Valuation Report Filed', due: '2026-07-15', status: 'complete', completion: 100 },
      { id: 'ms-005', name: '80% Funding Target Achieved', due: '2026-08-25', status: 'in-progress', completion: 79 },
      { id: 'ms-006', name: 'Escrow Release Conditions Verified', due: '2026-09-05', status: 'upcoming', completion: 0 },
      { id: 'ms-007', name: 'Final Investor Register Locked', due: '2026-09-12', status: 'upcoming', completion: 0 },
      { id: 'ms-008', name: 'Settlement & Fund Transfer', due: '2026-09-20', status: 'upcoming', completion: 0 },
    ],
    documents: [
      { id: 'doc-001', name: 'Information Memorandum v3.2', type: 'Disclosure', access: 'Public', size: '4.2 MB', uploaded: '2026-05-01', required: true, status: 'uploaded' },
      { id: 'doc-002', name: 'Institutional Investor Deck', type: 'Marketing', access: 'NDA-Gated', size: '8.7 MB', uploaded: '2026-05-10', required: false, status: 'uploaded' },
      { id: 'doc-003', name: 'Independent Valuation Report', type: 'Due Diligence', access: 'NDA-Gated', size: '2.1 MB', uploaded: '2026-07-14', required: true, status: 'uploaded' },
      { id: 'doc-004', name: 'Audited Financial Statements FY2025', type: 'Financial', access: 'NDA-Gated', size: '3.5 MB', uploaded: '2026-05-03', required: true, status: 'uploaded' },
      { id: 'doc-005', name: 'Legal Due Diligence Pack', type: 'Legal', access: 'Compliance Only', size: '6.8 MB', uploaded: '2026-05-05', required: true, status: 'uploaded' },
      { id: 'doc-006', name: 'Subscription Agreement Template', type: 'Agreement', access: 'Investors', size: '0.9 MB', uploaded: '2026-05-08', required: true, status: 'uploaded' },
    ],
    reviewNotes: [
      { stage: 'Compliance Review', reviewer: 'Fatima Al-Rashidi', date: '2026-05-12', note: 'All disclosure documents verified. FATCA/CRS declarations complete. Jurisdiction cleared for DIFC offering.', status: 'approved' },
      { stage: 'Risk Review', reviewer: 'James Okonkwo', date: '2026-05-18', note: 'Risk score: Medium. Concentration risk noted — single asset. Recommend 15% max per investor. Approved with conditions.', status: 'approved' },
      { stage: 'Finance Review', reviewer: 'Priya Nair', date: '2026-05-22', note: 'Escrow structure verified. Fee schedule confirmed at 2%. Waterfall distribution model reviewed and approved.', status: 'approved' },
    ],
  },
  {
    id: 'CAMP-2026-002',
    name: 'Solaris Renewable Energy Fund III',
    issuer: 'Solaris Asset Management DIFC',
    stage: 'Risk Review',
    target: 25000000,
    raised: 0,
    minInvestment: 50000,
    maxInvestment: 2000000,
    sector: 'Renewable Energy',
    jurisdiction: 'UAE (ADGM)',
    offeringType: 'Fund Units — Reg S',
    created: '2026-07-15',
    lastUpdated: '2026-08-18',
    description: 'Closed-end fund targeting utility-scale solar projects across MENA. 7-year term with quarterly distributions.',
    useOfFunds: '80% project equity · 12% working capital · 8% fund expenses',
    milestones: [
      { id: 'ms-101', name: 'Fund Prospectus Approved by Regulator', due: '2026-08-30', status: 'in-progress', completion: 60 },
      { id: 'ms-102', name: 'Anchor Investor Commitments (30%)', due: '2026-09-15', status: 'upcoming', completion: 0 },
      { id: 'ms-103', name: 'First Close (50% Target)', due: '2026-10-31', status: 'upcoming', completion: 0 },
      { id: 'ms-104', name: 'Final Close', due: '2026-12-31', status: 'upcoming', completion: 0 },
    ],
    documents: [
      { id: 'doc-101', name: 'Fund Prospectus v1.4', type: 'Disclosure', access: 'Public', size: '5.1 MB', uploaded: '2026-07-20', required: true, status: 'uploaded' },
      { id: 'doc-102', name: 'Risk Disclosure Statement', type: 'Disclosure', access: 'Public', size: '1.2 MB', uploaded: '2026-07-20', required: true, status: 'uploaded' },
      { id: 'doc-103', name: 'Audited Financials FY2025', type: 'Financial', access: 'NDA-Gated', size: '4.0 MB', uploaded: '2026-07-22', required: true, status: 'uploaded' },
      { id: 'doc-104', name: 'Environmental Impact Assessment', type: 'Due Diligence', access: 'NDA-Gated', size: '7.3 MB', uploaded: '2026-07-25', required: false, status: 'uploaded' },
      { id: 'doc-105', name: 'Subscription Agreement', type: 'Agreement', access: 'Investors', size: '1.1 MB', uploaded: '2026-07-28', required: true, status: 'uploaded' },
      { id: 'doc-106', name: 'AML/KYC Policy Document', type: 'Compliance', access: 'Compliance Only', size: '0.8 MB', uploaded: '2026-07-28', required: true, status: 'pending' },
    ],
    reviewNotes: [
      { stage: 'Compliance Review', reviewer: 'Fatima Al-Rashidi', date: '2026-08-05', note: 'Prospectus reviewed. Minor amendment required on risk factor disclosures — resubmitted and cleared.', status: 'approved' },
      { stage: 'Risk Review', reviewer: 'James Okonkwo', date: '2026-08-18', note: 'Reviewing concentration in single geography (MENA). Awaiting updated stress-test scenarios from issuer.', status: 'pending' },
    ],
  },
  {
    id: 'CAMP-2026-003',
    name: 'Nexus PropTech Growth Capital',
    issuer: 'Nexus Ventures MENA',
    stage: 'Draft',
    target: 5000000,
    raised: 0,
    minInvestment: 5000,
    maxInvestment: 250000,
    sector: 'PropTech / Real Estate',
    jurisdiction: 'UAE (Mainland)',
    offeringType: 'Convertible Note',
    created: '2026-08-10',
    lastUpdated: '2026-08-20',
    description: 'Convertible note raise for a B2B PropTech SaaS platform serving GCC real estate developers. 18-month term, 8% coupon, 20% discount on conversion.',
    useOfFunds: '55% product development · 30% sales & marketing · 15% operations',
    milestones: [
      { id: 'ms-201', name: 'Information Memorandum Drafted', due: '2026-08-25', status: 'in-progress', completion: 70 },
      { id: 'ms-202', name: 'Legal Counsel Review Complete', due: '2026-09-01', status: 'upcoming', completion: 0 },
      { id: 'ms-203', name: 'Submit for Compliance Review', due: '2026-09-05', status: 'upcoming', completion: 0 },
    ],
    documents: [
      { id: 'doc-201', name: 'Draft Information Memorandum', type: 'Disclosure', access: 'Compliance Only', size: '2.8 MB', uploaded: '2026-08-15', required: true, status: 'uploaded' },
      { id: 'doc-202', name: 'Pitch Deck v2.0', type: 'Marketing', access: 'NDA-Gated', size: '6.2 MB', uploaded: '2026-08-16', required: false, status: 'uploaded' },
      { id: 'doc-203', name: 'Audited Financials FY2025', type: 'Financial', access: 'NDA-Gated', size: '1.9 MB', uploaded: '2026-08-17', required: true, status: 'uploaded' },
      { id: 'doc-204', name: 'Subscription Agreement', type: 'Agreement', access: 'Investors', size: '0.7 MB', uploaded: '2026-08-18', required: true, status: 'pending' },
      { id: 'doc-205', name: 'Risk Disclosure Statement', type: 'Disclosure', access: 'Public', size: '0.5 MB', uploaded: '2026-08-18', required: true, status: 'pending' },
    ],
    reviewNotes: [],
  },
  {
    id: 'CAMP-2026-004',
    name: 'Harborview Private Credit Fund',
    issuer: 'Harborview Capital DIFC',
    stage: 'Compliance Review',
    target: 40000000,
    raised: 0,
    minInvestment: 100000,
    maxInvestment: 5000000,
    sector: 'Private Credit',
    jurisdiction: 'UAE (DIFC)',
    offeringType: 'Debt — Senior Secured',
    created: '2026-06-20',
    lastUpdated: '2026-08-14',
    description: 'Senior secured private credit fund targeting SME lending across GCC. 5-year term, target 12% net IRR.',
    useOfFunds: '90% loan origination · 7% reserves · 3% fund expenses',
    milestones: [
      { id: 'ms-301', name: 'Offering Circular Finalized', due: '2026-08-20', status: 'in-progress', completion: 85 },
      { id: 'ms-302', name: 'Compliance Approval', due: '2026-09-01', status: 'upcoming', completion: 0 },
      { id: 'ms-303', name: 'Risk & Finance Approval', due: '2026-09-15', status: 'upcoming', completion: 0 },
      { id: 'ms-304', name: 'Campaign Launch', due: '2026-10-01', status: 'upcoming', completion: 0 },
    ],
    documents: [
      { id: 'doc-301', name: 'Offering Circular v2.1', type: 'Disclosure', access: 'Public', size: '6.4 MB', uploaded: '2026-07-01', required: true, status: 'uploaded' },
      { id: 'doc-302', name: 'Credit Policy & Underwriting Standards', type: 'Compliance', access: 'Compliance Only', size: '3.2 MB', uploaded: '2026-07-05', required: true, status: 'uploaded' },
      { id: 'doc-303', name: 'Audited Financials FY2025', type: 'Financial', access: 'NDA-Gated', size: '5.1 MB', uploaded: '2026-07-08', required: true, status: 'uploaded' },
      { id: 'doc-304', name: 'Legal Opinion Letter', type: 'Legal', access: 'Compliance Only', size: '1.4 MB', uploaded: '2026-07-10', required: true, status: 'rejected' },
      { id: 'doc-305', name: 'Subscription Agreement', type: 'Agreement', access: 'Investors', size: '1.2 MB', uploaded: '2026-07-12', required: true, status: 'uploaded' },
    ],
    reviewNotes: [
      { stage: 'Compliance Review', reviewer: 'Fatima Al-Rashidi', date: '2026-08-14', note: 'Legal Opinion Letter (doc-304) rejected — must be re-issued by DIFC-registered counsel. Issuer notified.', status: 'rejected' },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const stageVariantMap: Record<WorkflowStage, string> = {
  'Draft': 'draft',
  'Compliance Review': 'review',
  'Risk Review': 'warning',
  'Finance Review': 'pending',
  'Approved': 'kyc-approved',
  'Published': 'published',
  'Closed': 'closed',
};

const stageColorMap: Record<WorkflowStage, string> = {
  'Draft': 'bg-surface-3 text-muted-foreground border-border',
  'Compliance Review': 'bg-info/10 text-info border-info/30',
  'Risk Review': 'bg-warning/10 text-warning border-warning/30',
  'Finance Review': 'bg-accent/10 text-accent border-accent/30',
  'Approved': 'bg-positive/10 text-positive border-positive/30',
  'Published': 'bg-primary/10 text-primary border-primary/30',
  'Closed': 'bg-surface-4 text-muted-foreground border-border',
};

const docStatusColor: Record<string, string> = {
  uploaded: 'text-positive',
  pending: 'text-warning',
  rejected: 'text-critical',
};

const reviewNoteIcon = (status: ReviewNote['status']) => {
  if (status === 'approved') return <CheckCircle size={14} className="text-positive flex-shrink-0 mt-0.5" />;
  if (status === 'rejected') return <XCircle size={14} className="text-critical flex-shrink-0 mt-0.5" />;
  if (status === 'pending') return <Clock size={14} className="text-warning flex-shrink-0 mt-0.5" />;
  return <Info size={14} className="text-info flex-shrink-0 mt-0.5" />;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function WorkflowTracker({ stage }: { stage: WorkflowStage }) {
  const currentIdx = workflowOrder.indexOf(stage);
  return (
    <div className="flex items-center gap-0 overflow-x-auto pb-1">
      {workflowOrder.map((s, i) => {
        const isComplete = i < currentIdx;
        const isCurrent = i === currentIdx;
        return (
          <React.Fragment key={`wf-${s}`}>
            <div className="flex flex-col items-center flex-shrink-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 border ${
                isComplete ? 'bg-primary/20 text-primary border-primary/40' : isCurrent ?'bg-primary text-white border-primary shadow-glow': 'bg-surface-3 text-muted-foreground border-border'
              }`}>
                {isComplete ? <CheckCircle size={13} /> : i + 1}
              </div>
              <p className={`text-xs mt-1.5 whitespace-nowrap ${
                isCurrent ? 'text-primary font-semibold' :
                isComplete ? 'text-muted-foreground' : 'text-muted-foreground/40'
              }`}>{s}</p>
            </div>
            {i < workflowOrder.length - 1 && (
              <div className={`h-0.5 flex-1 mx-1 min-w-3 ${isComplete ? 'bg-primary/40' : 'bg-border'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function MilestoneList({ milestones }: { milestones: Milestone[] }) {
  return (
    <div className="space-y-2.5">
      {milestones.map((ms) => (
        <div key={ms.id} className={`rounded-lg p-3 border ${
          ms.status === 'complete' ? 'border-primary/20 bg-primary/5' :
          ms.status === 'in-progress'? 'border-accent/30 bg-accent/5' : 'border-border bg-surface-3'
        }`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5 flex-1">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                ms.status === 'complete' ? 'bg-primary/20 text-primary' :
                ms.status === 'in-progress'? 'bg-accent/20 text-accent' : 'bg-surface-4 text-muted-foreground'
              }`}>
                {ms.status === 'complete' ? <CheckCircle size={12} /> :
                 ms.status === 'in-progress' ? <RefreshCw size={11} /> :
                 <Clock size={11} />}
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${ms.status === 'complete' ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                  {ms.name}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Due: <span className="font-mono-data">{ms.due}</span></p>
                {ms.status === 'in-progress' && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="progress-bar flex-1 max-w-40">
                      <div className="progress-bar-fill" style={{ width: `${ms.completion}%` }} />
                    </div>
                    <span className="text-xs font-mono-data text-accent">{ms.completion}%</span>
                  </div>
                )}
              </div>
            </div>
            <StatusBadge
              variant={ms.status === 'complete' ? 'settled' : ms.status === 'in-progress' ? 'review' : 'closed'}
              label={ms.status === 'complete' ? 'Complete' : ms.status === 'in-progress' ? 'In Progress' : 'Upcoming'}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function DocumentVault({ documents }: { documents: DisclosureDoc[] }) {
  const requiredCount = documents.filter(d => d.required).length;
  const uploadedRequired = documents.filter(d => d.required && d.status === 'uploaded').length;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Required documents: <span className={`font-mono-data font-semibold ${uploadedRequired === requiredCount ? 'text-positive' : 'text-warning'}`}>{uploadedRequired}/{requiredCount}</span> uploaded
        </p>
        <button className="btn-secondary text-xs gap-1.5"><Upload size={12} /> Upload Document</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface-3">
            <tr>
              {['Document', 'Type', 'Access', 'Size', 'Uploaded', 'Status', ''].map((h) => (
                <th key={`doc-th-${h}`} className="table-th">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => (
              <tr key={doc.id} className="data-table-row">
                <td className="table-td">
                  <div className="flex items-center gap-2">
                    <Paperclip size={13} className="text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{doc.name}</p>
                      {doc.required && <span className="text-xs text-warning">Required</span>}
                    </div>
                  </div>
                </td>
                <td className="table-td">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-surface-4 text-muted-foreground">{doc.type}</span>
                </td>
                <td className="table-td">
                  <div className="flex items-center gap-1">
                    {doc.access === 'NDA-Gated' || doc.access === 'Compliance Only' ? <Lock size={11} className="text-warning" /> : null}
                    <span className="text-xs text-muted-foreground">{doc.access}</span>
                  </div>
                </td>
                <td className="table-td font-mono-data text-xs text-muted-foreground">{doc.size}</td>
                <td className="table-td font-mono-data text-xs text-muted-foreground">{doc.uploaded}</td>
                <td className="table-td">
                  <span className={`text-xs font-semibold capitalize ${docStatusColor[doc.status]}`}>
                    {doc.status}
                  </span>
                </td>
                <td className="table-td">
                  <div className="flex items-center gap-1">
                    <button className="btn-ghost p-1" title="Download"><Download size={13} /></button>
                    <button className="btn-ghost p-1" title="Delete"><Trash2 size={13} className="text-critical/70" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReviewTimeline({ notes, stage }: { notes: ReviewNote[]; stage: WorkflowStage }) {
  const currentIdx = workflowOrder.indexOf(stage);
  const reviewStages: WorkflowStage[] = ['Compliance Review', 'Risk Review', 'Finance Review'];
  return (
    <div className="space-y-3">
      {reviewStages.map((rs) => {
        const rsIdx = workflowOrder.indexOf(rs);
        const note = notes.find(n => n.stage === rs);
        const isReached = rsIdx <= currentIdx;
        const isCurrent = rs === stage;
        return (
          <div key={`review-${rs}`} className={`rounded-lg p-3.5 border ${
            !isReached ? 'border-border bg-surface-3 opacity-50' : isCurrent ?'border-accent/30 bg-accent/5' :
            note?.status === 'approved' ? 'border-positive/20 bg-positive/5' :
            note?.status === 'rejected'? 'border-critical/20 bg-critical/5' : 'border-border bg-surface-2'
          }`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  note?.status === 'approved' ? 'bg-positive/20 text-positive' :
                  note?.status === 'rejected'? 'bg-critical/20 text-critical' : isCurrent ?'bg-accent/20 text-accent': 'bg-surface-4 text-muted-foreground'
                }`}>
                  {note?.status === 'approved' ? <Check size={12} /> :
                   note?.status === 'rejected' ? <X size={12} /> :
                   isCurrent ? <Clock size={12} /> :
                   <ChevronRight size={12} />}
                </div>
                <p className="text-sm font-semibold text-foreground">{rs}</p>
              </div>
              {note && (
                <StatusBadge
                  variant={note.status === 'approved' ? 'kyc-approved' : note.status === 'rejected' ? 'rejected' : 'review'}
                  label={note.status === 'approved' ? 'Approved' : note.status === 'rejected' ? 'Rejected' : 'In Review'}
                />
              )}
              {!note && isReached && <StatusBadge variant="review" label="In Review" />}
              {!isReached && <StatusBadge variant="closed" label="Pending" />}
            </div>
            {note ? (
              <div className="ml-8 space-y-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{note.reviewer}</span>
                  <span>·</span>
                  <span className="font-mono-data">{note.date}</span>
                </div>
                <div className="flex items-start gap-2 mt-1.5">
                  {reviewNoteIcon(note.status)}
                  <p className="text-xs text-muted-foreground leading-relaxed">{note.note}</p>
                </div>
              </div>
            ) : isReached ? (
              <p className="ml-8 text-xs text-muted-foreground italic">Review in progress — awaiting officer decision.</p>
            ) : (
              <p className="ml-8 text-xs text-muted-foreground/50 italic">Not yet reached.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Campaign Edit / Create Form ──────────────────────────────────────────────

interface CampaignFormProps {
  campaign?: Campaign | null;
  onClose: () => void;
  onSave: (data: Partial<Campaign>) => void;
}

function CampaignForm({ campaign, onClose, onSave }: CampaignFormProps) {
  const [name, setName] = useState(campaign?.name ?? '');
  const [issuer, setIssuer] = useState(campaign?.issuer ?? '');
  const [sector, setSector] = useState(campaign?.sector ?? '');
  const [jurisdiction, setJurisdiction] = useState(campaign?.jurisdiction ?? '');
  const [offeringType, setOfferingType] = useState(campaign?.offeringType ?? '');
  const [target, setTarget] = useState(campaign?.target ? String(campaign.target) : '');
  const [minInv, setMinInv] = useState(campaign?.minInvestment ? String(campaign.minInvestment) : '');
  const [maxInv, setMaxInv] = useState(campaign?.maxInvestment ? String(campaign.maxInvestment) : '');
  const [description, setDescription] = useState(campaign?.description ?? '');
  const [useOfFunds, setUseOfFunds] = useState(campaign?.useOfFunds ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ name, issuer, sector, jurisdiction, offeringType, target: Number(target), minInvestment: Number(minInv), maxInvestment: Number(maxInv), description, useOfFunds });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-surface-2 border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-surface-2 z-10">
          <h2 className="text-lg font-bold text-foreground">{campaign ? 'Edit Campaign' : 'Create New Campaign'}</h2>
          <button onClick={onClose} className="btn-ghost p-1.5"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Campaign Name *</label>
              <input value={name} onChange={e => setName(e.target.value)} required placeholder="e.g. Meridian Logistics Hub — Series B" className="input-field w-full" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Issuer / Company Name *</label>
              <input value={issuer} onChange={e => setIssuer(e.target.value)} required placeholder="e.g. Meridian Capital Partners Ltd." className="input-field w-full" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Sector *</label>
              <select value={sector} onChange={e => setSector(e.target.value)} required className="input-field w-full">
                <option value="">Select sector</option>
                {['Logistics & Infrastructure', 'Renewable Energy', 'PropTech / Real Estate', 'Private Credit', 'Technology', 'Healthcare', 'Financial Services', 'Consumer Goods'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Jurisdiction *</label>
              <select value={jurisdiction} onChange={e => setJurisdiction(e.target.value)} required className="input-field w-full">
                <option value="">Select jurisdiction</option>
                {['UAE (DIFC)', 'UAE (ADGM)', 'UAE (Mainland)', 'Saudi Arabia (CMA)', 'Bahrain (CBB)', 'UK (FCA)', 'Cayman Islands'].map(j => (
                  <option key={j} value={j}>{j}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Offering Type *</label>
              <select value={offeringType} onChange={e => setOfferingType(e.target.value)} required className="input-field w-full">
                <option value="">Select offering type</option>
                {['Equity — Reg D / Reg S', 'Fund Units — Reg S', 'Convertible Note', 'Debt — Senior Secured', 'Revenue Share', 'SAFE'].map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Funding Target (USD) *</label>
              <input type="number" value={target} onChange={e => setTarget(e.target.value)} required min="0" placeholder="e.g. 12500000" className="input-field w-full font-mono-data" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Min Investment (USD) *</label>
              <input type="number" value={minInv} onChange={e => setMinInv(e.target.value)} required min="0" placeholder="e.g. 10000" className="input-field w-full font-mono-data" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Max Investment (USD)</label>
              <input type="number" value={maxInv} onChange={e => setMaxInv(e.target.value)} min="0" placeholder="e.g. 500000" className="input-field w-full font-mono-data" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Campaign Description *</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} required rows={3} placeholder="Describe the campaign, business, and investment opportunity..." className="input-field w-full resize-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Use of Funds *</label>
              <textarea value={useOfFunds} onChange={e => setUseOfFunds(e.target.value)} required rows={2} placeholder="e.g. 60% construction · 25% working capital · 15% regulatory" className="input-field w-full resize-none" />
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary gap-1.5"><Save size={14} /> {campaign ? 'Save Changes' : 'Create Campaign'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Campaign Detail Panel ────────────────────────────────────────────────────

interface DetailPanelProps {
  campaign: Campaign;
  onClose: () => void;
  onEdit: () => void;
  onAdvanceStage: () => void;
}

function CampaignDetailPanel({ campaign, onClose, onEdit, onAdvanceStage }: DetailPanelProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'milestones' | 'documents' | 'review'>('overview');
  const currentIdx = workflowOrder.indexOf(campaign.stage);
  const canAdvance = currentIdx < workflowOrder.length - 1 && campaign.stage !== 'Published' && campaign.stage !== 'Closed';
  const nextStage = canAdvance ? workflowOrder[currentIdx + 1] : null;

  const fundedPct = campaign.target > 0 ? Math.round((campaign.raised / campaign.target) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-surface-2 border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <h2 className="text-lg font-bold text-foreground">{campaign.name}</h2>
              <StatusBadge variant={stageVariantMap[campaign.stage] as any} label={campaign.stage} />
            </div>
            <p className="text-xs text-muted-foreground font-mono-data">{campaign.id} · {campaign.issuer}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={onEdit} className="btn-secondary text-xs gap-1.5"><Edit3 size={13} /> Edit</button>
            {canAdvance && nextStage && (
              <button onClick={onAdvanceStage} className="btn-primary text-xs gap-1.5">
                <ArrowRight size={13} /> Submit to {nextStage}
              </button>
            )}
            <button onClick={onClose} className="btn-ghost p-1.5"><X size={18} /></button>
          </div>
        </div>

        {/* Workflow tracker */}
        <div className="px-6 py-3 border-b border-border bg-surface-3 flex-shrink-0">
          <WorkflowTracker stage={campaign.stage} />
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-border overflow-x-auto flex-shrink-0">
          {(['overview', 'milestones', 'documents', 'review'] as const).map((tab) => (
            <button key={`detail-tab-${tab}`} onClick={() => setActiveTab(tab)}
              className={`portal-tab capitalize ${activeTab === tab ? 'active' : ''}`}>
              {tab === 'review' ? 'Approval Review' : tab}
              {tab === 'documents' && campaign.documents.some(d => d.status === 'rejected') && (
                <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-critical inline-block" />
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === 'overview' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCard label="Funding Target" value={`$${(campaign.target / 1000000).toFixed(1)}M`} variant="default" icon={DollarSign} iconColor="text-primary" mono />
                <MetricCard label="Raised" value={campaign.raised > 0 ? `$${(campaign.raised / 1000000).toFixed(2)}M` : '—'} variant={campaign.raised > 0 ? 'positive' : 'default'} icon={TrendingUp} iconColor="text-positive" mono />
                <MetricCard label="Min Investment" value={`$${(campaign.minInvestment / 1000).toFixed(0)}K`} variant="default" icon={Users} iconColor="text-info" mono />
                <MetricCard label="Max Investment" value={campaign.maxInvestment ? `$${(campaign.maxInvestment / 1000).toFixed(0)}K` : 'No limit'} variant="default" icon={Building2} iconColor="text-accent" mono />
              </div>

              {campaign.raised > 0 && (
                <div className="glass-card rounded-xl p-4">
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-muted-foreground font-semibold uppercase tracking-wider">Funding Progress</span>
                    <span className="font-mono-data font-bold text-primary">{fundedPct}%</span>
                  </div>
                  <div className="funding-thermometer">
                    <div className="funding-thermometer-fill" style={{ width: `${fundedPct}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    ${(campaign.raised / 1000000).toFixed(2)}M raised of ${(campaign.target / 1000000).toFixed(1)}M target
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="glass-card rounded-xl p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Campaign Details</p>
                  {[
                    { label: 'Sector', value: campaign.sector },
                    { label: 'Jurisdiction', value: campaign.jurisdiction },
                    { label: 'Offering Type', value: campaign.offeringType },
                    { label: 'Created', value: campaign.created },
                    { label: 'Last Updated', value: campaign.lastUpdated },
                  ].map(({ label, value }) => (
                    <div key={`detail-${label}`} className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-medium text-foreground text-right">{value}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-3">
                  <div className="glass-card rounded-xl p-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Description</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{campaign.description}</p>
                  </div>
                  <div className="glass-card rounded-xl p-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Use of Funds</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{campaign.useOfFunds}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'milestones' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <SectionHeader title="Campaign Milestones" subtitle={`${campaign.milestones.length} milestones tracked`} />
                <button className="btn-secondary text-xs gap-1.5"><Plus size={13} /> Add Milestone</button>
              </div>
              <MilestoneList milestones={campaign.milestones} />
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-4">
              <SectionHeader title="Disclosure Documents" subtitle="Required and optional documents for this campaign" />
              <DocumentVault documents={campaign.documents} />
            </div>
          )}

          {activeTab === 'review' && (
            <div className="space-y-4">
              <SectionHeader title="Approval Stage Review" subtitle="Compliance → Risk → Finance review gates" />
              <ReviewTimeline notes={campaign.reviewNotes} stage={campaign.stage} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CampaignPortal() {
  const [campaignList, setCampaignList] = useState<Campaign[]>(campaigns);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null | undefined>(undefined);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<WorkflowStage | 'All'>('All');

  const filtered = campaignList.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.issuer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStage = stageFilter === 'All' || c.stage === stageFilter;
    return matchSearch && matchStage;
  });

  const handleAdvanceStage = (campaign: Campaign) => {
    const currentIdx = workflowOrder.indexOf(campaign.stage);
    if (currentIdx < workflowOrder.length - 1) {
      const nextStage = workflowOrder[currentIdx + 1];
      setCampaignList(prev => prev.map(c => c.id === campaign.id ? { ...c, stage: nextStage } : c));
      setSelectedCampaign(prev => prev?.id === campaign.id ? { ...prev, stage: nextStage } : prev);
    }
  };

  const handleSaveCampaign = (data: Partial<Campaign>) => {
    if (editingCampaign) {
      setCampaignList(prev => prev.map(c => c.id === editingCampaign.id ? { ...c, ...data } : c));
      setSelectedCampaign(prev => prev?.id === editingCampaign.id ? { ...prev, ...data } as Campaign : prev);
    } else {
      const newCampaign: Campaign = {
        id: `CAMP-2026-00${campaignList.length + 1}`,
        name: data.name ?? 'New Campaign',
        issuer: data.issuer ?? '',
        stage: 'Draft',
        target: data.target ?? 0,
        raised: 0,
        minInvestment: data.minInvestment ?? 0,
        maxInvestment: data.maxInvestment ?? 0,
        sector: data.sector ?? '',
        jurisdiction: data.jurisdiction ?? '',
        offeringType: data.offeringType ?? '',
        created: new Date().toISOString().split('T')[0],
        lastUpdated: new Date().toISOString().split('T')[0],
        description: data.description ?? '',
        useOfFunds: data.useOfFunds ?? '',
        milestones: [],
        documents: [],
        reviewNotes: [],
      };
      setCampaignList(prev => [newCampaign, ...prev]);
    }
    setEditingCampaign(undefined);
    setShowCreateForm(false);
  };

  // Stage counts for KPI strip
  const stageCounts = workflowOrder.reduce((acc, s) => {
    acc[s] = campaignList.filter(c => c.stage === s).length;
    return acc;
  }, {} as Record<WorkflowStage, number>);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Campaign Portal</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Create, edit, and publish investment campaigns through the full approval workflow</p>
        </div>
        <button onClick={() => { setEditingCampaign(null); setShowCreateForm(true); }} className="btn-primary gap-1.5">
          <Plus size={15} /> New Campaign
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {workflowOrder.map((s) => (
          <button
            key={`kpi-${s}`}
            onClick={() => setStageFilter(stageFilter === s ? 'All' : s)}
            className={`rounded-xl p-3 border text-left transition-all ${
              stageFilter === s ? `${stageColorMap[s]} ring-1 ring-current` : 'glass-card border-border hover:border-primary/30'
            }`}
          >
            <p className="text-xs text-muted-foreground mb-1 truncate">{s}</p>
            <p className="text-xl font-bold font-mono-data text-foreground">{stageCounts[s]}</p>
          </button>
        ))}
      </div>

      {/* Search & filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search campaigns, issuers, IDs..."
            className="input-field w-full pl-9 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-muted-foreground" />
          <select value={stageFilter} onChange={e => setStageFilter(e.target.value as any)} className="input-field text-sm">
            <option value="All">All Stages</option>
            {workflowOrder.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <p className="text-xs text-muted-foreground">{filtered.length} campaign{filtered.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Campaign table */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-3">
              <tr>
                {['Campaign', 'Issuer', 'Stage', 'Funding Target', 'Progress', 'Sector', 'Jurisdiction', 'Updated', 'Actions'].map(h => (
                  <th key={`th-${h}`} className="table-th">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="table-td text-center py-12 text-muted-foreground">
                    No campaigns match your search or filter.
                  </td>
                </tr>
              ) : filtered.map((c) => {
                const fundedPct = c.target > 0 ? Math.round((c.raised / c.target) * 100) : 0;
                const hasDocIssue = c.documents.some(d => d.status === 'rejected');
                return (
                  <tr key={c.id} className="data-table-row cursor-pointer" onClick={() => setSelectedCampaign(c)}>
                    <td className="table-td">
                      <div className="flex items-center gap-2">
                        {hasDocIssue && <AlertCircle size={13} className="text-critical flex-shrink-0" title="Document issue" />}
                        <div>
                          <p className="font-medium text-foreground text-sm">{c.name}</p>
                          <p className="text-xs font-mono-data text-muted-foreground">{c.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-td text-sm text-muted-foreground">{c.issuer}</td>
                    <td className="table-td">
                      <StatusBadge variant={stageVariantMap[c.stage] as any} label={c.stage} />
                    </td>
                    <td className="table-td font-mono-data font-semibold text-foreground">
                      ${(c.target / 1000000).toFixed(1)}M
                    </td>
                    <td className="table-td">
                      {c.raised > 0 ? (
                        <div className="flex items-center gap-2 min-w-24">
                          <div className="progress-bar flex-1">
                            <div className="progress-bar-fill" style={{ width: `${fundedPct}%` }} />
                          </div>
                          <span className="text-xs font-mono-data text-primary font-semibold">{fundedPct}%</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="table-td">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-surface-4 text-muted-foreground">{c.sector}</span>
                    </td>
                    <td className="table-td text-xs text-muted-foreground">{c.jurisdiction}</td>
                    <td className="table-td font-mono-data text-xs text-muted-foreground">{c.lastUpdated}</td>
                    <td className="table-td" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelectedCampaign(c)} className="btn-ghost p-1" title="View"><Eye size={13} /></button>
                        <button onClick={() => { setEditingCampaign(c); setShowCreateForm(true); }} className="btn-ghost p-1" title="Edit"><Edit3 size={13} /></button>
                        {workflowOrder.indexOf(c.stage) < workflowOrder.length - 1 && c.stage !== 'Published' && c.stage !== 'Closed' && (
                          <button onClick={() => handleAdvanceStage(c)} className="btn-ghost p-1" title={`Submit to ${workflowOrder[workflowOrder.indexOf(c.stage) + 1]}`}>
                            <Send size={13} className="text-primary" />
                          </button>
                        )}
                        <button className="btn-ghost p-1" title="More"><MoreVertical size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail panel */}
      {selectedCampaign && (
        <CampaignDetailPanel
          campaign={selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
          onEdit={() => { setEditingCampaign(selectedCampaign); setShowCreateForm(true); }}
          onAdvanceStage={() => handleAdvanceStage(selectedCampaign)}
        />
      )}

      {/* Create / Edit form */}
      {showCreateForm && (
        <CampaignForm
          campaign={editingCampaign ?? null}
          onClose={() => { setShowCreateForm(false); setEditingCampaign(undefined); }}
          onSave={handleSaveCampaign}
        />
      )}
    </div>
  );
}
